import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from 'reactstrap';
import { isValidIBAN as isIbanValid } from 'ibantools';
import { useForm } from 'react-hook-form';
import { errorMessages } from '../../../constants/messages';

const AddBankDetails = ({ toggle, isOpen, onConfirm, loading, bankList, currencyList, countryList, user }) => {
  const { register, handleSubmit, errors, setValue, reset } = useForm({
    defaultValues: {
      bank_country: '',
      bank_id: '',
      bank_name: '',
      swift: '',
      ifsc_or_aba: '',
      pay_currency_id: '',
      pay_currency: '',
      number_type: 1,
      iban_no: null,
      account_no: '',
    },
  });
  const [noBankListed, setNoBankListed] = useState(false);
  const [numberType, setNumberType] = useState(1);
  const [ifsc, setIfsc] = useState(false);
  const [ifscOrAba, setIfscOrAba] = useState('IFSC Code');
  const [bankListData, setBankListData] = useState([]);
  const [isIBANStrictCountry, setIsIBANStrictCountry] = useState(false);
  const [isIBANandACnumber, setIsIBANandACnumber] = useState(false);
  const ibanRadioRef = useRef('number_type');
  const [isValidIBAN, setIsValidIBAN] = useState(true);

  const onIBANChanged = (value) => {
    setIsValidIBAN(isIbanValid(value));
    if (isIbanValid(value)) {
      setValue('iban_no', value, { shouldValidate: true, shouldDirty: true });
    }
  };

  const onSelectAccountNumberType = (value) => {
    setNumberType(value);
    setValue('iban_no', '', { shouldValidate: true, shouldDirty: true });
    setValue('number_type', value);
  };

  const onSelectBank = (e) => {
    if (e.target.value === '0') {
      // Not selected
      setNoBankListed(false);
      setValue('bank_name', null, { shouldValidate: true, shouldDirty: true });
      setValue('bank_id', null, { shouldValidate: true, shouldDirty: true });
    } else if (e.target.value === '-1' || e.target.value === '-2') {
      // intl-bank or bank not listed
      setNoBankListed(true);
      setValue('bank_id', e.target.value, { shouldValidate: true, shouldDirty: true });
      setValue('bank_name', null, { shouldValidate: true, shouldDirty: true });
      setValue('swift', '', { shouldValidate: true, shouldDirty: true });
    } else {
      setNoBankListed(false);
      const bank = bankList.find((obj) => obj.BankAutoId === parseInt(e.target.value, 10));
      setValue('bank_name', bank?.Title, { shouldValidate: true, shouldDirty: true });
      setValue('bank_id', e.target.value, { shouldValidate: true, shouldDirty: true });
      setValue('swift', bank?.SwiftCode, { shouldValidate: true, shouldDirty: true });
    }
  };

  const onSelectBankCountry = (e) => {
    if (e.target.value === '0') {
      setValue('bank_country', null, { shouldValidate: true, shouldDirty: true });
      return;
    }

    // Filters the bank list based on country id (international bank and bank not listed options also)
    const bank = bankList.filter(
      (obj) => obj.country_id === parseInt(e.target.value, 10) || obj.country_id === null || obj.country_id === 0,
    );

    setBankListData(bank);
    setValue('bank_country', e.target.value, { shouldValidate: true, shouldDirty: true });
    setValue('bank_name', null, { shouldValidate: true, shouldDirty: true });
    setValue('bank_id', null, { shouldValidate: true, shouldDirty: true });
    setValue('swift', null, { shouldValidate: true, shouldDirty: true });
    setIsIBANStrictCountry(false);
    setIsIBANandACnumber(false);

    // Check if Oman is selected (show both fields without radio)
    if (e.target.value === '166') {
      // Oman
      setIsIBANandACnumber(true);
      setNumberType(3);
      setValue('number_type', 3);
      setValue('iban_no', '', { shouldValidate: true, shouldDirty: true });
      setValue('account_no', '', { shouldValidate: true, shouldDirty: true });
    } else if (['234', '118', '179', '18'].includes(e.target.value)) {
      setIsIBANStrictCountry(true);
      onSelectAccountNumberType(1); // For IBAN
      ibanRadioRef.current.checked = true;
      setValue('iban_no', '', { shouldValidate: true, shouldDirty: true });
      setValue('account_no', '');
    }

    setIfsc(false);
    if (e.target.value === '102') {
      // For India
      setIfsc(true);
      setIfscOrAba('IFSC Code');
    } else if (e.target.value === '236') {
      // For USA
      setIfsc(true);
      setIfscOrAba('ABA Code');
    }
  };

  const onSelectCurrency = (e) => {
    const x = currencyList.find((obj) => String(obj.id) === String(e.target.value)) || {};
    setValue('pay_currency_id', e.target.value);
    setValue('pay_currency', x?.currency);
  };

  const onConfirmSubmission = async (formData) => {
    const payload = { ...formData, number_type: numberType };

    if (numberType === 1) {
      payload.account_no = '';
    } else if (numberType === 2) {
      payload.iban_no = '';
    } else if (numberType === 3) {
      payload.iban_no = formData.iban_no || '';
      payload.account_no = formData.account_no || '';
    }

    if (numberType === 1 || numberType === 3) {
      if (!isIbanValid(payload.iban_no)) {
        setIsValidIBAN(false);
        return;
      }
    }

    const success = await onConfirm(payload);
    if (success) {
      reset();
      toggle();
    }
  };

  const handleCustomSubmit = () => {
    if ((numberType === 1 && isValidIBAN) || numberType === 2 || numberType === 3) {
      handleSubmit(onConfirmSubmission)();
    }
  };

  useEffect(() => {
    setBankListData(bankList);
  }, [bankList]);

  useEffect(() => {
    if (isOpen) {
      setIsIBANStrictCountry(false);
      setIsIBANandACnumber(false);
      setIfsc(false);
      setBankListData(bankList);
      reset();
    }
  }, [isOpen]);

  return (
    <div>
      <Modal size="lg" isOpen={isOpen} toggle={toggle} style={{ width: '80vw' }}>
        <Form className="">
          <ModalHeader toggle={toggle}>Add Bank details</ModalHeader>
          <ModalBody className="pt-4 mt-3">
            <Row>
              <Col className="col-md-6 col-12">
                <FormGroup className="form-label-group">
                  <Input
                    type="select"
                    name="bank_country"
                    id="bank_country"
                    placeholder="Country"
                    disabled={loading}
                    invalid={!!errors?.bank_country}
                    innerRef={register('bank_country', { required: errorMessages.missingData })}
                    onChange={onSelectBankCountry}
                  >
                    <option key="0" value="0">
                      ---select country---
                    </option>
                    {countryList?.map((item) => (
                      <option key={item?.id} value={item?.id}>
                        {item?.short_name}
                      </option>
                    ))}
                  </Input>
                  <Label>Country</Label>
                </FormGroup>
              </Col>
              <Col className="col-md-6 col-12">
                <FormGroup className="form-label-group">
                  <Input
                    type="select"
                    name="bank_id"
                    id="bank_id"
                    placeholder="Bank Name"
                    disabled={loading}
                    invalid={!!errors?.bank_id}
                    innerRef={register('bank_id', {
                      required: errorMessages.missingData,
                    })}
                    onChange={onSelectBank}
                  >
                    {bankListData?.map((item) => (
                      <option key={item?.BankAutoId} value={item?.BankAutoId}>
                        {item?.Title}
                      </option>
                    ))}
                  </Input>
                  <Label>Bank Name</Label>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col className="col-md-6 col-12">
                <FormGroup className={noBankListed ? 'form-label-group' : 'd-none'}>
                  <Input
                    type={noBankListed ? 'text' : 'hidden'}
                    name="bank_name"
                    id="bank_name"
                    placeholder="Enter your bank name"
                    disabled={loading}
                    invalid={!!errors?.bank_name}
                    innerRef={register('bank_name', { required: errorMessages.missingData })}
                    onInput={(e) => setValue('bank_name', e.target.value, { shouldValidate: true, shouldDirty: true })}
                  />
                  <Label>Enter your bank name</Label>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col className="col-md-6 col-12">
                <FormGroup className="form-label-group">
                  <Input
                    type="text"
                    name="swift"
                    id="swift"
                    placeholder="Swift Code"
                    disabled={loading}
                    invalid={!!errors?.swift}
                    innerRef={register({ required: errorMessages.missingData })}
                    onChange={(e) => setValue('swift', e.target.value, { shouldValidate: true, shouldDirty: true })}
                  />
                  <Label>Swift Code</Label>
                </FormGroup>
              </Col>
              {ifsc ? (
                <Col className="col-md-6 col-12">
                  <FormGroup className="form-label-group">
                    <Input
                      type="text"
                      name="ifsc_or_aba"
                      id="ifsc_or_aba"
                      placeholder="IFSC/ABA Code"
                      disabled={loading}
                      invalid={!!errors?.ifsc_or_aba}
                      innerRef={register('ifsc_or_aba', { required: errorMessages.missingData })}
                      onChange={(e) =>
                        setValue('ifsc_or_aba', e.target.value, { shouldValidate: true, shouldDirty: true })
                      }
                    />
                    <Label>{ifscOrAba}</Label>
                  </FormGroup>
                </Col>
              ) : (
                <Col className="col-md-6 col-12">
                  <FormGroup className="form-label-group">
                    <Input
                      className="custom-select"
                      type="select"
                      name="pay_currency_id"
                      id="pay_currency_id"
                      placeholder="Payment currency"
                      disabled={loading}
                      invalid={!!errors?.pay_currency_id}
                      innerRef={register('pay_currency_id', { required: errorMessages.missingData })}
                      onChange={onSelectCurrency}
                    >
                      <option key="0" value="">
                        ---Select currency---
                      </option>
                      {currencyList?.map((item) => (
                        <option key={item?.id} value={item?.id}>
                          {item?.name}
                        </option>
                      ))}
                    </Input>
                    <Input
                      type="hidden"
                      name="pay_currency"
                      id="pay_currency"
                      placeholder="Payment currency"
                      disabled={loading}
                      invalid={!!errors?.pay_currency}
                      innerRef={register('pay_currency', { required: errorMessages.missingData })}
                      onChange={(e) =>
                        setValue('pay_currency', e.target.value, { shouldValidate: true, shouldDirty: true })
                      }
                    />
                    <Label>Payment currency</Label>
                  </FormGroup>
                </Col>
              )}
            </Row>
            <Row>
              <Col className="col-md-6 col-12">
                <FormGroup className="form-label-group">
                  <Input
                    type="text"
                    name="payable_to"
                    id="payable_to"
                    placeholder="Account Holder Name"
                    defaultValue={user?.member_name}
                    disabled
                    invalid={!!errors?.payable_to}
                    innerRef={register({ required: errorMessages.missingData })}
                    onChange={(e) =>
                      setValue('payable_to', e.target.value, { shouldValidate: true, shouldDirty: true })
                    }
                  />
                  <Label>Account Holder Name</Label>
                </FormGroup>
              </Col>
              <Col className="col-md-6 col-12">
                {ifsc ? (
                  <FormGroup className="form-label-group">
                    <Input
                      className="custom-select"
                      type="select"
                      name="pay_currency_id"
                      id="pay_currency_id"
                      placeholder="Payment currency"
                      disabled={loading}
                      invalid={!!errors?.pay_currency_id}
                      innerRef={register('pay_currency_id', { required: errorMessages.missingData })}
                      onChange={onSelectCurrency}
                    >
                      <option key="0">---Select currency---</option>
                      {currencyList?.map((item) => (
                        <option key={item?.id} value={item?.id}>
                          {item?.currency}
                        </option>
                      ))}
                    </Input>
                    <Input
                      type="hidden"
                      name="pay_currency"
                      id="pay_currency"
                      placeholder="Payment currency"
                      disabled={loading}
                      invalid={!!errors?.pay_currency}
                      innerRef={register('pay_currency', { required: errorMessages.missingData })}
                      onChange={(e) =>
                        setValue('pay_currency', e.target.value, { shouldValidate: true, shouldDirty: true })
                      }
                    />
                    <Label>Payment currency</Label>
                  </FormGroup>
                ) : (
                  ''
                )}
              </Col>
            </Row>
            <Row>
              <Col className="d-flex mb-3">
                {!isIBANandACnumber ? (
                  <>
                    <FormGroup check className="mr-4">
                      <Label check>
                        <Input
                          type="radio"
                          name="number_type"
                          value="1"
                          defaultChecked={numberType === 1}
                          disabled={loading}
                          innerRef={(e) => {
                            ibanRadioRef.current = e;
                          }}
                          onChange={() => onSelectAccountNumberType(1)}
                        />
                        IBAN
                      </Label>
                    </FormGroup>
                    {!isIBANStrictCountry ? (
                      <FormGroup check>
                        <Label check>
                          <Input
                            type="radio"
                            name="number_type"
                            value="2"
                            defaultChecked={numberType === 2}
                            disabled={loading}
                            onChange={() => onSelectAccountNumberType(2)}
                          />
                          Account Number
                        </Label>
                      </FormGroup>
                    ) : null}
                  </>
                ) : null}
              </Col>
            </Row>
            <Row>
              <Col className="col-md-6 col-12">
                {isIBANandACnumber ? (
                  <>
                    <FormGroup className="form-label-group">
                      <Input
                        type="text"
                        name="iban_no"
                        id="iban_no"
                        placeholder="Enter IBAN"
                        disabled={loading}
                        invalid={!!errors?.iban_no}
                        innerRef={register({ required: errorMessages.missingData })}
                        onChange={(e) => onIBANChanged(e.target.value)}
                      />
                      <Label>Enter IBAN</Label>
                      {!isValidIBAN ? (
                        <p style={{ fontSize: '14px', color: 'red', marginTop: '5px' }}>Invalid IBAN</p>
                      ) : null}
                    </FormGroup>
                    <FormGroup className="form-label-group">
                      <Input
                        type="text"
                        name="account_no"
                        id="account_no"
                        placeholder="Enter Account Number"
                        disabled={loading}
                        invalid={!!errors?.account_no}
                        innerRef={register({ required: errorMessages.missingData })}
                        onChange={(e) =>
                          setValue('account_no', e.target.value, { shouldValidate: true, shouldDirty: true })
                        }
                      />
                      <Label>Enter Account Number</Label>
                    </FormGroup>
                  </>
                ) : numberType === 1 ? (
                  <FormGroup className="form-label-group">
                    <Input
                      type="text"
                      name="iban_no"
                      id="iban_no"
                      placeholder="Enter IBAN"
                      disabled={loading}
                      invalid={!!errors?.iban_no}
                      innerRef={register({ required: errorMessages.missingData })}
                      onChange={(e) => onIBANChanged(e.target.value)}
                    />
                    <Label>Enter IBAN</Label>
                    {!isValidIBAN ? (
                      <p style={{ fontSize: '14px', color: 'red', marginTop: '5px' }}>Invalid IBAN</p>
                    ) : null}
                  </FormGroup>
                ) : (
                  <FormGroup className="form-label-group">
                    <Input
                      type="text"
                      name="account_no"
                      id="account_no"
                      placeholder="Enter Account Number"
                      disabled={loading}
                      invalid={!!errors?.account_no}
                      innerRef={register({ required: errorMessages.missingData })}
                      onChange={(e) =>
                        setValue('account_no', e.target.value, { shouldValidate: true, shouldDirty: true })
                      }
                    />
                    <Label>Enter Account Number</Label>
                  </FormGroup>
                )}
              </Col>
            </Row>
            <Row>
              <Col className="note">
                <p className="alert alert-dark mb-3">
                  Note: Please ensure that the bank details you provide will be used for reimbursement of your claims.
                  Therefore please ensure that the details provided are accurate and up-to-date.
                  <br />
                  <br />
                  Note: If your bank is not listed you can select international bank option
                </p>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter className="justify-content-center">
            <Button color="outline-primary" onClick={toggle}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleCustomSubmit} disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" color="light" />
                  <span>&nbsp;Loading</span>
                </>
              ) : (
                'Confirm'
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

AddBankDetails.propTypes = {
  toggle: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  bankList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  currencyList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  countryList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  user: PropTypes.shape({
    member_name: PropTypes.string,
  }).isRequired,
};

AddBankDetails.defaultProps = {
  isOpen: false,
  loading: false,
};

export default withRouter(AddBankDetails);
